import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Form, Formik, FormikProps } from "formik";
import { toast } from "react-toastify";
import { QUERY_KEYS } from "../../../../app/data/common/queryKeys";
import PickupRequestsService from "../../../../app/data/pickup-requests/pickupRequestsService";
import {
  BolAddressBlock,
  BolDocument,
  FinalBOLResult,
} from "../../../../app/data/pickup-requests/bolModels";
import { PickupReviewStatus } from "../../../../app/data/communications/models";
import { ApprovePickupReviewRequest } from "../../../../app/data/pickup-requests/models";
import { buildApprovePickupReviewRequest } from "../utils/approvePickupBodyUtils";
import {
  bolDocToFormValues,
  BolReviewFormValues,
  applyShipperEmailFallbackToBolDocument,
  createEmptyBolSnapshot,
  syncFormToEditedBol,
} from "../utils/bolReviewMappers";
import { BolReviewFormSchema } from "../utils/bolReviewValidation";
import {
  getFirstFormErrorMessage,
  touchAllBolReviewFields,
} from "../utils/bolFormValidationUtils";
import { INBOX_TRANSACTIONAL_QUERY_OPTIONS } from "../utils/pickupInboxQueryOptions";
import Button, { ButtonThemes } from "../../../../ui-components/button/button";
import Loading from "../../../../ui-components/loading/loading";
import BolFieldInput from "./bolFieldInput";
import BolCapturedBolReviewFields from "./bolCapturedBolReviewFields";
import RequesterCompanyField from "./requesterCompanyField";
import PickupRequestReviewAudit from "./pickupRequestReviewAudit";
import AiConfidenceBar from "./aiConfidenceBar";
import {
  averageBolConfidence,
  collectBolIssues,
  countAutoFilledFields,
} from "../utils/bolFormIssues";

const pickupRequestsService = PickupRequestsService.getInstance();

const FIGMA_FIELD = { variant: "figma" as const, compact: true };

export interface CapturedBolFormProps {
  pickupRequestId: number;
  reviewStatus: PickupReviewStatus;
  confidenceScore?: number;
  pickupNumber?: string;
  senderEmail?: string;
  onReviewComplete?: () => void;
  onRequesterTeamIdChange?: (teamId: string | null) => void;
}

const invalidateInboxAfterReview = async (
  queryClient: ReturnType<typeof useQueryClient>,
  pickupRequestId: number
) => {
  await Promise.all([
    queryClient.invalidateQueries([QUERY_KEYS.PICKUP_INBOX_MESSAGES]),
    queryClient.invalidateQueries([QUERY_KEYS.PICKUP_INBOX_STATS]),
    queryClient.invalidateQueries([QUERY_KEYS.PICKUP_INBOX_MESSAGE_DETAIL]),
    queryClient.invalidateQueries([QUERY_KEYS.PICKUP_INBOX_BOL_SNAPSHOT, pickupRequestId]),
    queryClient.invalidateQueries([QUERY_KEYS.PICKUP_INBOX_PICKUP_REQUEST, pickupRequestId]),
  ]);
};

interface CapturedBolReadOnlyBodyProps {
  doc: BolDocument;
}

const CapturedBolReadOnlyBody: React.FC<CapturedBolReadOnlyBodyProps> = ({ doc }) => {
  const items = doc.items ?? [];

  const renderPartyColumn = (
    title: string,
    block: "pickupFrom" | "shipTo",
    data?: BolAddressBlock
  ) => (
    <div className="xgs-captured-bol__party-column">
      <h4 className="xgs-captured-bol__party-heading">{title}</h4>
      <BolFieldInput label="Company Name" field={data?.name} readOnly required placeholder="Company Name" {...FIGMA_FIELD} />
      <BolFieldInput label="Address" field={data?.addressLine_1} readOnly required placeholder="Street Address" {...FIGMA_FIELD} />
      <BolFieldInput label="" field={data?.addressLine_2} readOnly placeholder="Suite, Unit, Building (Optional)" showAiBadge={false} {...FIGMA_FIELD} />
      <div className="xgs-captured-bol__party-row xgs-captured-bol__party-row--3">
        <BolFieldInput label="City" field={data?.city} readOnly required placeholder="City" {...FIGMA_FIELD} />
        <BolFieldInput label="State" field={data?.state} readOnly required placeholder="CA" {...FIGMA_FIELD} />
        <BolFieldInput label="ZIP" field={data?.zip} readOnly required placeholder="ZIP" {...FIGMA_FIELD} />
      </div>
      <BolFieldInput label="Phone" field={data?.phoneNumber} readOnly required placeholder="(000) 000-0000" {...FIGMA_FIELD} />
      <BolFieldInput label="Email" field={data?.email} readOnly required placeholder="email@example.com" {...FIGMA_FIELD} />
    </div>
  );

  return (
    <>
      <div className="xgs-captured-bol__row xgs-captured-bol__row--cols-3">
        <BolFieldInput label="BOL Number" field={doc.bol} readOnly required placeholder="Enter BOL Number" {...FIGMA_FIELD} />
        <BolFieldInput label="PO Number" field={doc.purchaseOrder} readOnly {...FIGMA_FIELD} />
        <BolFieldInput label="Additional Reference" field={doc.orderDescription} readOnly {...FIGMA_FIELD} />
      </div>

      <div className="xgs-captured-bol__parties">
        {renderPartyColumn("Shipper (From)", "pickupFrom", doc.pickupFrom)}
        {renderPartyColumn("Consignee (To)", "shipTo", doc.shipTo)}
      </div>

      <div className="xgs-captured-bol__row xgs-captured-bol__row--cols-3 xgs-captured-bol__row--muted-light">
        <BolFieldInput label="Pickup Date" field={doc.pickupWindow?.windowDate} readOnly required {...FIGMA_FIELD} />
        <BolFieldInput label="Ready Time" field={doc.pickupWindow?.windowStartTime} readOnly required {...FIGMA_FIELD} />
        <BolFieldInput label="Close Time" field={doc.pickupWindow?.windowEndTime} readOnly required {...FIGMA_FIELD} />
      </div>

      <div className="xgs-captured-bol__commodity">
        <div className="xgs-captured-bol__commodity-header">
          <span>Commodity Information</span>
        </div>
        <div className="xgs-captured-bol__table-wrap">
          <table className="xgs-captured-bol__table xgs-captured-bol__table--figma">
            <thead>
              <tr>
                <th>PKG Type</th>
                <th>Units</th>
                <th>Weight (lbs)</th>
                <th>Class</th>
                <th>Description</th>
                <th>L (in)</th>
                <th>W (in)</th>
                <th>H (in)</th>
                <th>Notes</th>
                <th>Sidemark</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={10} className="xgs-captured-bol__table-empty">
                    No line items extracted
                  </td>
                </tr>
              ) : (
                items.map((item, idx) => (
                  <tr key={idx}>
                    <td>
                      <BolFieldInput label="" field={item.type} readOnly compact showAiBadge={false} placeholder="PALLET" variant="figma" />
                    </td>
                    <td>
                      <BolFieldInput label="" field={item.numberOfUnits} readOnly compact showAiBadge={false} type="number" variant="figma" />
                    </td>
                    <td>
                      <BolFieldInput label="" field={item.weight} readOnly compact showAiBadge={false} type="number" variant="figma" />
                    </td>
                    <td>
                      <BolFieldInput label="" field={item.classCode} readOnly compact showAiBadge={false} placeholder="50-500" variant="figma" />
                    </td>
                    <td>
                      <BolFieldInput label="" field={item.description} readOnly compact showAiBadge={false} placeholder="Item description" variant="figma" />
                    </td>
                    <td>
                      <BolFieldInput label="" field={item.length} readOnly compact showAiBadge={false} type="number" variant="figma" />
                    </td>
                    <td>
                      <BolFieldInput label="" field={item.width} readOnly compact showAiBadge={false} type="number" variant="figma" />
                    </td>
                    <td>
                      <BolFieldInput label="" field={item.height} readOnly compact showAiBadge={false} type="number" variant="figma" />
                    </td>
                    <td>
                      <BolFieldInput label="" field={item.notes} readOnly compact showAiBadge={false} placeholder="Additional notes" variant="figma" />
                    </td>
                    <td>
                      <BolFieldInput label="" field={item.sidemark} readOnly compact showAiBadge={false} placeholder="Sidemark" variant="figma" />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="xgs-captured-bol__instructions">
        <BolFieldInput
          label="Special Instructions"
          field={doc.generalNotes}
          readOnly
          multiline
          placeholder="Enter any special handling instructions or delivery notes..."
          {...FIGMA_FIELD}
        />
      </div>
    </>
  );
};

const CapturedBolForm: React.FC<CapturedBolFormProps> = ({
  pickupRequestId,
  reviewStatus,
  confidenceScore,
  pickupNumber,
  senderEmail,
  onReviewComplete,
  onRequesterTeamIdChange,
}) => {
  const queryClient = useQueryClient();
  const [editedBol, setEditedBol] = useState<FinalBOLResult | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [showReject, setShowReject] = useState(false);
  const [requesterTeamId, setRequesterTeamId] = useState<string | null>(null);
  const [requesterReady, setRequesterReady] = useState(false);
  const [isReviewActionPending, setIsReviewActionPending] = useState(false);
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  const isReviewMode = reviewStatus === "REQUIRE_HUMAN_REVIEW";

  const { data: snapshot, isLoading, isFetching, isError } = useQuery({
    queryKey: [QUERY_KEYS.PICKUP_INBOX_BOL_SNAPSHOT, pickupRequestId],
    queryFn: () => pickupRequestsService.getBolSnapshot(pickupRequestId),
    retry: false,
    ...INBOX_TRANSACTIONAL_QUERY_OPTIONS,
  });

  const { data: pickupRequest } = useQuery({
    queryKey: [QUERY_KEYS.PICKUP_INBOX_PICKUP_REQUEST, pickupRequestId],
    queryFn: () => pickupRequestsService.getPickupRequest(pickupRequestId),
    retry: false,
    ...INBOX_TRANSACTIONAL_QUERY_OPTIONS,
  });

  useEffect(() => {
    setEditedBol(null);
  }, [pickupRequestId]);

  const bolQuerySettled = !isLoading && !isFetching;
  const isManualEntry = bolQuerySettled && !snapshot;

  const workingEditedBol = useMemo((): FinalBOLResult | null => {
    if (editedBol) return editedBol;
    if (snapshot) {
      const copy = JSON.parse(JSON.stringify(snapshot)) as FinalBOLResult;
      copy.bol_document = applyShipperEmailFallbackToBolDocument(copy.bol_document, senderEmail);
      return copy;
    }
    if (isManualEntry && isReviewMode) {
      const empty = createEmptyBolSnapshot();
      empty.bol_document = applyShipperEmailFallbackToBolDocument(empty.bol_document, senderEmail);
      return empty;
    }
    return null;
  }, [editedBol, snapshot, senderEmail, isManualEntry, isReviewMode]);

  const onRequesterTeamChange = useCallback((teamId: string | null) => {
    setRequesterTeamId(teamId);
    onRequesterTeamIdChange?.(teamId);
  }, [onRequesterTeamIdChange]);

  const onRequesterReadyChange = useCallback((ready: boolean) => {
    setRequesterReady(ready);
  }, []);

  const handleFormSync = useCallback((values: BolReviewFormValues) => {
    setEditedBol((prev) => syncFormToEditedBol(values, prev ?? createEmptyBolSnapshot()));
  }, []);

  const buildApproveBody = useCallback((): ApprovePickupReviewRequest => {
    if (!workingEditedBol) {
      throw new Error("BOL data is not available.");
    }

    return buildApprovePickupReviewRequest(
      workingEditedBol as unknown as Record<string, unknown>,
      workingEditedBol.bol_document,
      requesterTeamId
    );
  }, [workingEditedBol, requesterTeamId]);

  const runPostReviewInvalidation = useCallback(async () => {
    setIsReviewActionPending(true);
    try {
      await invalidateInboxAfterReview(queryClient, pickupRequestId);
    } finally {
      setIsReviewActionPending(false);
    }
  }, [queryClient, pickupRequestId]);

  const approveMutation = useMutation({
    mutationFn: () => {
      const body = buildApproveBody();
      if (!body.teamId) {
        throw new Error("Requester company is required before creating the pickup.");
      }
      if (!body.pickupDate) {
        throw new Error("Pickup date is required before creating the pickup.");
      }
      return pickupRequestsService.approveReview(pickupRequestId, body);
    },
    onSuccess: async () => {
      toast.info("Pickup approved and created.");
      await runPostReviewInvalidation();
      onReviewComplete?.();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const rejectMutation = useMutation({
    mutationFn: () =>
      pickupRequestsService.rejectReview(pickupRequestId, {
        reason: rejectReason,
        correctedPayload: (editedBol ?? workingEditedBol) as unknown as Record<string, unknown>,
      }),
    onSuccess: async () => {
      toast.info("Pickup request rejected.");
      await runPostReviewInvalidation();
      onReviewComplete?.();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const doc = workingEditedBol?.bol_document;
  const issues = useMemo(() => (doc ? collectBolIssues(doc) : []), [doc]);
  const autoFilledCount = useMemo(() => (doc ? countAutoFilledFields(doc) : 0), [doc]);
  const displayConfidence = useMemo(() => {
    if (confidenceScore != null) return confidenceScore;
    if (doc) return averageBolConfidence(doc);
    return null;
  }, [confidenceScore, doc]);

  const formInitialValues = useMemo(
    () =>
      doc
        ? bolDocToFormValues(doc, { senderEmail })
        : bolDocToFormValues({} as BolDocument, { senderEmail }),
    [doc, senderEmail]
  );

  const showBolLoading = isLoading || (isFetching && !workingEditedBol);

  if (showBolLoading) return <Loading isLoading />;
  if (!workingEditedBol || !doc) {
    return (
      <div className="xgs-captured-bol__empty">
        {isError ? "Unable to load BOL data. Please try again." : "BOL data not available yet."}
      </div>
    );
  }

  const readOnly = !isReviewMode;
  const errorIssues = issues.filter((i) => i.severity === "error");
  const warnIssues = issues.filter((i) => i.severity === "warning");
  const reviewActionBusy =
    isReviewActionPending || approveMutation.isLoading || rejectMutation.isLoading;
  const overlayMessage = rejectMutation.isLoading
    ? "Rejecting request…"
    : "Creating pickup…";

  const handleCreatePickup = async (formik: FormikProps<BolReviewFormValues>) => {
    setShowValidationErrors(true);
    const errors = await formik.validateForm();
    formik.setTouched(touchAllBolReviewFields(formik.values));

    if (!requesterReady) {
      toast.error("Select a requester company before creating the pickup.");
      return;
    }

    if (Object.keys(errors).length > 0) {
      toast.error(
        getFirstFormErrorMessage(errors) ||
          "Please complete all required fields before creating the pickup."
      );
      return;
    }

    approveMutation.mutate();
  };

  const footerActions = (formik: FormikProps<BolReviewFormValues>) => (
    <div className="xgs-captured-bol__form-footer-actions">
      <Button
        theme={ButtonThemes.red}
        type="button"
        disabled={reviewActionBusy}
        onClick={() => setShowReject(true)}
      >
        Reject
      </Button>
      <button
        type="button"
        className="xgs-captured-bol__submit-btn"
        disabled={reviewActionBusy}
        onClick={() => handleCreatePickup(formik)}
      >
        Create pickup
      </button>
    </div>
  );

  return (
    <div className="xgs-captured-bol">
      {isReviewMode && !isManualEntry && (
        <div className="xgs-captured-bol__ai-banner">
          <div className="xgs-captured-bol__ai-banner-top">
            <div>
              <div className="xgs-captured-bol__ai-banner-title">
                {`AI Extraction Complete — ${issues.length} issue${issues.length === 1 ? "" : "s"} need your attention`}
              </div>
              <p className="xgs-captured-bol__ai-banner-text">
                Fields marked AI were pre-filled from email + BOL. Please verify and fix all issues before approving.
              </p>
            </div>
            <AiConfidenceBar score={displayConfidence} className="xgs-captured-bol__ai-confidence" />
          </div>
          <div className="xgs-captured-bol__issue-pills">
            {errorIssues.map((issue) => (
              <span key={issue.label} className="xgs-captured-bol__pill xgs-captured-bol__pill--error">
                {issue.label}
              </span>
            ))}
            {warnIssues.map((issue) => (
              <span key={issue.label} className="xgs-captured-bol__pill xgs-captured-bol__pill--warning">
                {issue.label}
              </span>
            ))}
            {autoFilledCount > 0 && (
              <span className="xgs-captured-bol__pill xgs-captured-bol__pill--success">
                {autoFilledCount} fields auto-filled
              </span>
            )}
          </div>
        </div>
      )}

      <div className="xgs-captured-bol__card xgs-captured-bol__card--relative">
        {reviewActionBusy && (
          <div className="xgs-captured-bol__loading-overlay" aria-busy="true">
            <Loading isLoading />
            <p className="xgs-captured-bol__loading-overlay-text">{overlayMessage}</p>
          </div>
        )}

        <div className="xgs-captured-bol__row xgs-captured-bol__row--muted">
          <RequesterCompanyField
            pickupRequestId={pickupRequestId}
            senderEmail={senderEmail}
            companyNameHint={
              doc.pickupFrom?.name?.value != null
                ? String(doc.pickupFrom.name.value)
                : undefined
            }
            readOnly={readOnly}
            showValidationErrors={showValidationErrors}
            preferExplicitTeamSelection={isManualEntry}
            onReadyChange={onRequesterReadyChange}
            onTeamChange={onRequesterTeamChange}
          />
        </div>

        {!isReviewMode && pickupRequest && (
          <PickupRequestReviewAudit
            pickupRequest={pickupRequest}
            pickupNumber={pickupNumber}
          />
        )}

        {isReviewMode ? (
          <Formik
            initialValues={formInitialValues}
            enableReinitialize
            validationSchema={BolReviewFormSchema}
            validateOnMount
            validateOnChange
            onSubmit={() => undefined}
          >
            {(formik: FormikProps<BolReviewFormValues>) => (
              <Form>
                <BolCapturedBolReviewFields
                  bolDoc={doc}
                  onFormSync={handleFormSync}
                  showExtractionHints={!isManualEntry}
                />

                <div className="xgs-captured-bol__form-footer">
                  <div className="xgs-captured-bol__form-footer-notes">
                    <p>* Required fields must be completed</p>
                    <p>All information subject to carrier verification</p>
                  </div>
                  {footerActions(formik)}
                </div>

                {showReject && (
                  <div className="xgs-captured-bol__reject-panel">
                    <textarea
                      className="xgs-captured-bol__notes"
                      placeholder="Rejection reason (required)"
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      disabled={reviewActionBusy}
                    />
                    <Button
                      theme={ButtonThemes.red}
                      type="button"
                      spinner={rejectMutation.isLoading}
                      disabled={!rejectReason.trim() || reviewActionBusy}
                      onClick={() => rejectMutation.mutate()}
                    >
                      Confirm reject
                    </Button>
                  </div>
                )}
              </Form>
            )}
          </Formik>
        ) : (
          <>
            <CapturedBolReadOnlyBody doc={doc} />
            <div className="xgs-captured-bol__form-footer">
              <div className="xgs-captured-bol__form-footer-notes">
                <p>* Required fields must be completed</p>
                <p>All information subject to carrier verification</p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CapturedBolForm;
